import { useEffect, useId, useState } from 'react';

import { Bell, Info, Mail, Save } from 'lucide-react';
import { toast } from 'sonner';

import {
  mensagemDoErro,
  usePreferenciasDeNotificacao,
  useSalvarPreferenciasDeNotificacao,
} from '@/modulos/notificacoes/dados';
import { ErrorState } from '@/ui/ErrorState';
import { FormPage } from '@/ui/FormPage';
import { PageHeader } from '@/ui/PageHeader';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { Skeleton } from '@/ui/base/skeleton';
import { Switch } from '@/ui/base/switch';

/**
 * Preferências de notificação de QUEM ESTÁ LOGADO.
 *
 * UMA TELA, UMA PREFERÊNCIA POR PESSOA. A mesma tela é montada em
 * `/credenciado/perfil/notificacoes`, `/admin/perfil/notificacoes`,
 * `/operacao/perfil/notificacoes` e `/sme/perfil/notificacoes`, dentro da casca
 * de cada área, e grava a mesma linha (`notification_preferences.user_id`,
 * `bff/v1/notificacoes/preferencias`): quem abre por uma área vê o que gravou
 * pela outra. Decisão do dono de 2026-10-02: tela única por usuário, com
 * gravação real.
 *
 * SÓ O QUE TEM EFEITO É OFERECIDO. Hoje a única preferência que alguma regra lê
 * é o e-mail das comunicações opcionais (comunicação de impacto da J-11; as
 * obrigatórias vão sempre). Canais sem provedor (WhatsApp, push), o SMS
 * (desligado pela M-16) e o silêncio por tipo e por categoria não são
 * renderizados (regra 7 de interface): gravá-los faria a tela afirmar uma regra
 * que não vale. O aviso fixo diz isso a quem procura. Ver
 * `apps/api/src/modulos/notificacoes/README.md`.
 */
export function PreferenciasNotificacaoPage({
  voltarPara = '/credenciado/notificacoes',
}: {
  /**
   * Para onde o "voltar" do formulário leva: a central de notificações no
   * portal; nas áreas internas, quem monta a rota passa a raiz da área.
   */
  voltarPara?: string;
} = {}) {
  const preferencias = usePreferenciasDeNotificacao();
  const salvar = useSalvarPreferenciasDeNotificacao();
  const idDoEmail = useId();

  const [email, setEmail] = useState<boolean | null>(null);

  useEffect(() => {
    if (preferencias.data && email === null) {
      setEmail(preferencias.data.email);
    }
  }, [preferencias.data, email]);

  const alterada = preferencias.data !== undefined && email !== preferencias.data.email;

  function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (email === null) return;
    salvar.mutate(
      { email },
      {
        onSuccess: () => toast.success('Preferências salvas.'),
        onError: (erro) =>
          toast.error('As preferências não foram salvas', {
            description: mensagemDoErro(erro, 'Tente de novo em instantes.'),
          }),
      },
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title="Preferências de notificação"
        description="Escolha como você quer ser avisado. A escolha vale para a sua conta, em qualquer área do sistema."
      />

      {preferencias.isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar suas preferências agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void preferencias.refetch() }}
        />
      ) : preferencias.isLoading || email === null ? (
        <div role="status" aria-live="polite" className="space-y-4">
          {Array.from({ length: 2 }).map((_, indice) => (
            <Skeleton key={indice} className="h-20 w-full rounded-2xl" />
          ))}
          <p className="sr-only">Carregando preferências de notificação.</p>
        </div>
      ) : (
        <FormPage onSubmit={enviar} backTo={voltarPara} maxWidth="max-w-2xl" hideFooter>
          <section aria-labelledby="canais-titulo" className="space-y-3">
            <h2 id="canais-titulo" className="text-sm font-semibold">
              Canais
            </h2>

            <div className="flex items-start gap-3 rounded-lg border p-3">
              <Bell className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">No sistema</p>
                <p className="text-xs text-muted-foreground">
                  Sempre ligado. Todo aviso fica no sino de notificações, onde você pode lê-lo
                  depois.
                </p>
              </div>
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Mail
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <Label
                    htmlFor={idDoEmail}
                    className="text-sm font-medium normal-case tracking-normal text-foreground"
                  >
                    E-mail das comunicações opcionais
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Desligado, você deixa de receber por e-mail as comunicações opcionais, como o
                    aviso de mudança de regra. Os e-mails obrigatórios continuam chegando.
                  </p>
                </div>
              </div>
              <Switch
                id={idDoEmail}
                checked={email}
                onCheckedChange={(valor) => setEmail(valor)}
                disabled={salvar.isPending}
              />
            </div>
          </section>

          <Alert>
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              Escolher tipos de aviso, silenciar categorias e receber por WhatsApp ou push ainda não
              está disponível.
            </AlertDescription>
          </Alert>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={!alterada || salvar.isPending}>
              <Save className="mr-2 h-4 w-4" aria-hidden="true" />
              {salvar.isPending ? 'Salvando...' : 'Salvar preferências'}
            </Button>
          </div>
        </FormPage>
      )}
    </div>
  );
}

export default PreferenciasNotificacaoPage;
